import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import PageHeader from '../components/PageHeader';
import { pricingPlans } from '../mock';
import { useLocale } from '../i18n/LocaleContext';

export default function PricingPage() {
  const { t, formatPrice, currency } = useLocale();
  return (
    <>
      <PageHeader
        index="// 04"
        eyebrow={t('pricing.eyebrow')}
        title={t('pricing.title')}
        accent={t('pricing.accent')}
        intro={t('pricing.intro')}
      />

      <section className="relative py-20 bg-surface overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bloom rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bloom rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 max-w-6xl mx-auto items-start">
            {pricingPlans.map((plan, i) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.55 }}
                className={`relative rounded-2xl border p-8 transition-all duration-300 ${
                  plan.highlighted
                    ? 'border-purple-500/50 bg-gradient-to-b from-purple-900/25 to-pink-900/15 shadow-2xl shadow-purple-500/20 md:-mt-4'
                    : 'border-line bg-raise hover:border-purple-500/40'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-mono-ui text-[11px] tracking-wider uppercase px-3 py-1 rounded-full shadow-lg">
                    <Sparkles className="w-3.5 h-3.5" />
                    {t('pricing.mostPopular')}
                  </div>
                )}

                <h3 className="font-display text-2xl font-semibold text-strong mb-1">{plan.name}</h3>
                <p className="text-sm text-soft mb-6">{plan.description}</p>

                <div className="flex items-baseline gap-2 mb-7">
                  <span className="font-display text-5xl font-bold ink-grad">
                    {plan.priceUSD != null ? formatPrice(plan.priceUSD) : t('pricing.custom')}
                  </span>
                  <span className="font-mono-ui text-sm text-faint">
                    / {plan.priceUSD != null ? t('pricing.oneTime') : t('pricing.customPricing')}
                  </span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-soft text-sm">
                      <Check className="w-5 h-5 text-purple-500 shrink-0 mt-px" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact" className="block">
                  <Button
                    className={`w-full py-6 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                      plan.highlighted
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg shadow-purple-500/30'
                        : 'bg-raise2 hover:bg-raise text-strong border border-line'
                    }`}
                  >
                    {t('cta.getStarted')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="text-center font-mono-ui text-sm text-faint mt-12">
            {t('pricing.note')}
            {currency !== 'USD' && <><br />{t('pricing.localNote')}</>}
          </p>
        </div>
      </section>
    </>
  );
}
